/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

@file:Suppress("TooManyFunctions")

package systems.frictionless.impulse.browser.style.support

import org.jetbrains.compose.web.css.CSSBorder
import org.jetbrains.compose.web.css.CSSNumeric
import org.jetbrains.compose.web.css.CSSSizeValue
import org.jetbrains.compose.web.css.CSSUnit
import org.jetbrains.compose.web.css.CSSUnitValue
import org.jetbrains.compose.web.css.SelectorsScope
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.selectors.CSSSelector
import org.jetbrains.compose.web.css.textAlign

// This is missing in standard library.
// Surprisingly there is StyleBuilder.marginTop but not StyleBuilder.marginBottom.
// Not sure why. So I added it here and we will remove it from here when added into standard
enum class FontStyle(internal val cssValue: String) {
    Normal("normal"),
    Italic("italic"),
    Oblique("oblique")
}

fun StyleScope.fontStyle(value: FontStyle?) {
    value?.let {
        property("font-style", value.cssValue)
    }
}

sealed class FontWeight(internal val cssValue: String) {
    object Normal : FontWeight("normal")

    object Bold : FontWeight("bold")

    class Number(value: Int) : FontWeight(value.toString())
}

fun StyleScope.fontWeight(value: FontWeight?) {
    value?.let {
        property("font-weight", value = value.cssValue)
    }
}

fun StyleScope.fontSize(value: String) {
    property("font-size", value)
}

fun StyleScope.marginBlockStart(value: CSSNumeric) {
    property("margin-block-start", value)
}

fun StyleScope.marginBlockEnd(value: CSSNumeric) {
    property("margin-block-end", value)
}

fun StyleScope.gridGap(value: CSSNumeric) {
    property("grid-gap", value)
}

fun StyleScope.visibility(value: Visibility) {
    property("visibility", value.cssValue)
}

fun StyleScope.textAlign(value: TextAlign) {
    textAlign(value.cssValue)
}

fun StyleScope.backgroundPositionX(value: CSSUnitValue) {
    property("background-position-x", value)
}

fun StyleScope.backgroundPositionY(value: CSSUnitValue) {
    property("background-position-y", value)
}

enum class Margin(val cssValue: String) {
    AUTO("auto"),
    UNSET("unset")
}

fun StyleScope.marginLeft(value: Margin) {
    property("margin-left", value.cssValue)
}

fun StyleScope.marginRight(value: Margin) {
    property("margin-right", value.cssValue)
}

fun StyleScope.marginTop(value: Margin) {
    property("margin-top", value.cssValue)
}

fun StyleScope.marginBottom(value: Margin) {
    property("margin-bottom", value.cssValue)
}

fun StyleScope.boxShadow(value: String) {
    property("box-shadow", value)
}

fun StyleScope.width(value: String) {
    property("width", value)
}

fun StyleScope.height(value: String) {
    property("height", value)
}

fun StyleScope.scrollbarWidth(value: String) {
    property("scrollbar-width", value)
}

fun StyleScope.paddingBottom(value: String) {
    property("padding-bottom", value)
}

fun StyleScope.content(value: String) {
    property("content", "\"$value\"")
}

fun StyleScope.padding(value: String) {
    property("padding", value)
}

fun repeat(
    cells: Int,
    flexFactor: CSSSizeValue<CSSUnit.fr>
): String {
    return "repeat($cells,$flexFactor)"
}

fun SelectorsScope.pseudoelement(elementName: String): CSSSelector {
    return selector("::$elementName")
}

fun SelectorsScope.pseudoclass(elementName: String): CSSSelector {
    return selector(":$elementName")
}

val SelectorsScope.placeholder: CSSSelector
    inline get() {
        return pseudoelement("placeholder")
    }

object Overflow {
    const val SCROLL = "scroll"
}

val SelectorsScope.webkitScrollbar: CSSSelector
    get() = pseudoelement("-webkit-scrollbar")
val SelectorsScope.webkitScrollbarThumb: CSSSelector
    get() = pseudoelement("-webkit-scrollbar-thumb")
val SelectorsScope.webkitScrollbarTrackPiece: CSSSelector
    get() = pseudoelement("-webkit-scrollbar-track-piece")

fun StyleScope.marginHorizontal(value: Margin) {
    marginLeft(value)
    marginRight(value)
}

fun StyleScope.marginVertical(value: CSSSizeValue<CSSUnit.px>) {
    marginTop(value)
    marginBottom(value)
}

fun StyleScope.marginVertical(value: Margin) {
    marginTop(value)
    marginBottom(value)
}

fun StyleScope.marginBlockStart(value: String) {
    property("margin-block-start", value)
}

fun StyleScope.marginBlockEnd(value: String) {
    property("margin-block-end", value)
}

fun StyleScope.borderRight(borderBuild: CSSBorder.() -> Unit) {
    property("border-right", CSSBorder().apply(borderBuild))
}

fun StyleScope.borderTop(borderBuild: CSSBorder.() -> Unit) {
    property("border-top", CSSBorder().apply(borderBuild))
}

fun StyleScope.borderBottom(borderBuild: CSSBorder.() -> Unit) {
    property("border-bottom", CSSBorder().apply(borderBuild))
}

fun StyleScope.borderTopLeftRadius(value: CSSUnitValue) {
    property("border-top-left-radius", value)
}

fun StyleScope.borderBottomLeftRadius(value: CSSUnitValue) {
    property("border-bottom-left-radius", value)
}

fun StyleScope.borderTopRightRadius(value: CSSUnitValue) {
    property("border-top-right-radius", value)
}

fun StyleScope.borderBottomRightRadius(value: CSSUnitValue) {
    property("border-bottom-right-radius", value)
}

fun StyleScope.position(value: String) {
    property("position", value)
}

fun StyleScope.top(value: String) {
    property("top", value)
}

fun StyleScope.left(value: String) {
    property("left", value)
}

fun StyleScope.transform(value: String) {
    property("transform", value)
}

/*
 * Css values
 */
enum class TextAlign(val cssValue: String) {
    LEFT("left"),
    RIGHT("right"),
    CENTER("center"),
    START("start")
}

enum class Visibility(val cssValue: String) {
    VISIBLE("visible"),
    HIDDEN("hidden")
}
